import React, { createContext, useContext, useMemo } from "react";
import {
  Box,
  ThemeProvider,
  createTheme,
  useTheme,
  type Theme,
} from "@mui/material";
import { useInterfaceDesign } from "./InterfaceDesign.js";
import { designTokens, interfaceFont } from "./tokens.js";

const OriginalTheme = createContext<Theme | null>(null);

/** Always keeps the same provider tree. No global CSS and no styling of other windows. */
export function DesignSurface({
  children,
  enabled = true,
  modeOverride,
  colorModeOverride,
}: {
  children: React.ReactNode;
  enabled?: boolean;
  /** Isolated composition previews can compare designs without changing the user's preference. */
  modeOverride?: "original" | "modern";
  colorModeOverride?: "light" | "dark";
}) {
  const inherited = useTheme();
  const original = useContext(OriginalTheme) ?? inherited;
  const preference = useInterfaceDesign();
  const mode = modeOverride ?? preference.mode;
  const colorMode = colorModeOverride ?? preference.colorMode;
  const modern = enabled && mode === "modern";
  const theme = useMemo(() => {
    if (!modern) return original;
    const p = designTokens[colorMode];
    return createTheme(original, {
      palette: {
        mode: colorMode,
        primary: {
          main: p.accent,
          contrastText: colorMode === "dark" ? "#17213a" : "#ffffff",
        },
        background: { default: p.chrome, paper: p.surface },
        text: { primary: p.text, secondary: p.muted, disabled: p.muted },
        divider: p.border,
        success: {
          main: p.success,
          contrastText: colorMode === "dark" ? "#17213a" : "#ffffff",
        },
        warning: {
          main: p.warning,
          contrastText: colorMode === "dark" ? "#17213a" : "#ffffff",
        },
        error: {
          main: p.error,
          contrastText: colorMode === "dark" ? "#17213a" : "#ffffff",
        },
        action: {
          selected: p.selected,
          hover: p.fill,
          active: p.muted,
          disabled: p.muted,
          disabledBackground: p.chrome,
        },
      },
      typography: {
        fontFamily: interfaceFont,
        fontSize: 12,
        button: { textTransform: "none", fontWeight: 500 },
        h1: { color: p.text },
        h2: { color: p.text },
        h3: { color: p.text },
        h4: { color: p.text },
        h5: { color: p.text },
        h6: { fontSize: 17, fontWeight: 600, color: p.text },
        body1: { color: p.text, fontSize: 12, lineHeight: 1.6 },
        body2: { color: p.muted, fontSize: 12, lineHeight: 1.6 },
        subtitle1: { color: p.text, fontSize: 14, fontWeight: 600 },
        subtitle2: { color: p.text, fontSize: 12, fontWeight: 600 },
        caption: { color: p.muted, fontSize: 11 },
        overline: { color: p.muted },
      },
      shape: { borderRadius: 8 },
      components: {
        MuiButton: {
          defaultProps: {
            size: "small",
            disableElevation: true,
            disableRipple: true,
          },
          styleOverrides: {
            root: {
              minHeight: 29,
              fontSize: 11,
              lineHeight: 1.35,
              borderRadius: 6,
              padding: "5px 9px",
              whiteSpace: "nowrap",
            },
          },
        },
        MuiIconButton: {
          defaultProps: { size: "small" },
          styleOverrides: {
            root: { borderRadius: 6 },
            sizeSmall: { padding: 5, "& svg": { fontSize: 16 } },
          },
        },
        MuiTextField: {
          defaultProps: { size: "small" },
          styleOverrides: { root: { minWidth: 0 } },
        },
        MuiFormControl: { defaultProps: { size: "small" } },
        MuiFormControlLabel: { styleOverrides: { label: { fontSize: 12 } } },
        MuiCheckbox: {
          defaultProps: { size: "small" },
          styleOverrides: { root: { "& svg": { fontSize: 17 } } },
        },
        MuiInputLabel: {
          defaultProps: { shrink: true },
          styleOverrides: {
            root: {
              position: "relative",
              transform: "none",
              fontSize: 10,
              lineHeight: 1.4,
              color: p.muted,
              marginBottom: 6,
              maxWidth: "100%",
              "&.MuiInputLabel-shrink": { transform: "none" },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              backgroundColor: p.surface,
              borderRadius: 6,
              fontSize: 12,
              minHeight: 33,
            },
            input: { padding: "7px 9px", height: 17, lineHeight: "17px" },
            notchedOutline: {
              borderColor: p.border,
              top: 0,
              "& legend": { display: "none" },
            },
          },
        },
        MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
        MuiBackdrop: {
          styleOverrides: {
            root: {
              backgroundColor:
                colorMode === "dark"
                  ? "rgba(20,21,24,.62)"
                  : "rgba(243,243,245,.65)",
              backdropFilter: "blur(4px)",
              "&.MuiBackdrop-invisible": {
                backgroundColor: "transparent",
                backdropFilter: "none",
              },
            },
          },
        },
        MuiDialog: {
          styleOverrides: {
            paper: {
              border: `1px solid ${p.border}`,
              borderRadius: 13,
              backgroundImage: "none",
            },
          },
        },
        MuiDialogTitle: {
          styleOverrides: {
            root: {
              padding: "17px 22px",
              fontSize: 17,
              fontWeight: 600,
              backgroundColor: p.chrome,
              borderBottom: `1px solid ${p.border}`,
            },
          },
        },
        MuiDialogContent: {
          styleOverrides: {
            root: {
              padding: "20px 22px",
              "&.MuiDialogContent-root": { paddingTop: 20 },
            },
          },
        },
        MuiDialogActions: {
          styleOverrides: {
            root: {
              padding: "12px 22px",
              backgroundColor: p.chrome,
              borderTop: `1px solid ${p.border}`,
              gap: 8,
            },
          },
        },
        MuiSelect: {
          defaultProps: {
            MenuProps: {
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
            },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              boxSizing: "border-box",
              border: `1px solid ${p.border}`,
              padding: 4,
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: { textTransform: "none", minHeight: 42, fontSize: 12 },
          },
        },
        MuiTabs: {
          styleOverrides: { root: { minHeight: 42 }, indicator: { height: 2 } },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: 5,
              fontWeight: 500,
              height: 22,
              fontSize: 10,
            },
          },
        },
        MuiButtonBase: {
          styleOverrides: {
            root: {
              "&.Mui-focusVisible": {
                outline: `2px solid ${p.accent}`,
                outlineOffset: 2,
              },
            },
          },
        },
      },
    });
  }, [modern, original, colorMode]);
  return (
    <OriginalTheme.Provider value={original}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </OriginalTheme.Provider>
  );
}

/** Saved compositions keep their original inherited typography and MUI styles. */
export function OriginalDesignSurface({
  children,
}: {
  children: React.ReactNode;
}) {
  const current = useTheme();
  const original = useContext(OriginalTheme) ?? current;
  return (
    <ThemeProvider theme={original}>
      <Box
        data-original-design-surface
        sx={{
          width: "100%",
          height: "100%",
          color: original.palette.text.primary,
          fontFamily: original.typography.fontFamily,
          fontSize: original.typography.fontSize,
        }}
      >
        {children}
      </Box>
    </ThemeProvider>
  );
}
